import { Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import type { GridDataSource } from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import type { DataCollectionSubmissionStatus } from "@entrilia/shared/api/dataCollectionTypes";
import type { DataCollectionSubmissionInfo } from "../../../../api/types/dataCollectionTypes";
import useClientContext from "../../../../context/useClientContext";
import { FilterContextProvider } from "../../../common/filters/FilterContextProvider";
import FiltersPanel from "../../../common/filters/FiltersPanel";
import { filterRecords, getInitialFilterState } from "../../../common/filters/filterState";
import DataSubmissionsGrid from "./DataSubmissionsGrid";
import {
  mySubmissionsFilterDefinitions,
  mySubmissionsSearchDefinition,
  selectOptionsResolver,
} from "./dataSubmissionsGridFilters";

interface Props {
  assignedDataSubmissions: DataCollectionSubmissionInfo[];
  loading: boolean;
  onRefresh: () => void;
}

const MyDataSubmissionsList = ({ assignedDataSubmissions, loading, onRefresh }: Props) => {
  const { clientCode } = useClientContext();

  const gridApiRef = useGridApiRef();

  const filterId = useMemo(() => `${clientCode}_my_data_submissions_filter_v1`, [clientCode]);

  const [statusFilterValue, setStatusFilterValue] = useState<DataCollectionSubmissionStatus>("Pending");

  const [filterState, setFilterState] = useState(() =>
    getInitialFilterState(filterId, mySubmissionsFilterDefinitions, [], [], mySubmissionsSearchDefinition)
  );

  const allSelectedSubmissions = useMemo(
    () => assignedDataSubmissions.filter((ds) => ds.status === statusFilterValue),
    [assignedDataSubmissions, statusFilterValue]
  );

  const filteredSubmissions = useMemo(
    () => filterRecords(filterState, allSelectedSubmissions),
    [allSelectedSubmissions, filterState]
  );

  const dataSource: GridDataSource = useMemo(
    () => ({
      getRows: () =>
        Promise.resolve({
          rows: filteredSubmissions,
          rowCount: filteredSubmissions.length,
        }),
    }),
    [filteredSubmissions]
  );

  return (
    <FilterContextProvider
      filterState={filterState}
      setFilterState={setFilterState}
      getSelectOptionsForFilter={selectOptionsResolver}
      allRowsForSelectOptions={assignedDataSubmissions}
    >
      <Stack spacing={2} sx={{ height: "100%" }}>
        <Stack direction="row" spacing={2}>
          <ToggleButtonGroup
            value={statusFilterValue}
            exclusive
            onChange={(_, value: DataCollectionSubmissionStatus) => setStatusFilterValue(value)}
            sx={{ height: "2.25rem" }}
          >
            <ToggleButton value="Pending" sx={{ gap: 1 }}>
              Active
            </ToggleButton>
            <ToggleButton value="Completed" sx={{ gap: 1 }}>
              Completed
            </ToggleButton>
          </ToggleButtonGroup>

          <FiltersPanel
            totalCount={allSelectedSubmissions.length}
            recordCount={filteredSubmissions.length}
            isLoading={loading}
            onRefresh={onRefresh}
          />
        </Stack>

        <DataSubmissionsGrid apiRef={gridApiRef} dataSource={dataSource} filterId={filterId} />
      </Stack>
    </FilterContextProvider>
  );
};

export default MyDataSubmissionsList;
